import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';

export interface ThemeConfig {
  enabled: boolean;
  mode: 'auto' | 'manual' | 'custom';
  activeTheme: string;
  emojis: string[];
  density: 'low' | 'medium' | 'high';
  speed: 'slow' | 'medium' | 'fast';
  wind: 'none' | 'light' | 'gentle';
}

export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  enabled: true,
  mode: 'auto',
  activeTheme: 'winter',
  emojis: ['❄️', '❅', '❆', '⛄', '✨'],
  density: 'medium',
  speed: 'medium',
  wind: 'light',
};

// Automatik-Berechnung nach aktuellem Datum
export function getAutoThemeEmojis(now = new Date()): { themeName: string; emojis: string[] } {
  const month = now.getMonth() + 1; // 1-12
  const day = now.getDate();

  // 1. Silvester & Neujahr (27.12. - 06.01.)
  if ((month === 12 && day >= 27) || (month === 1 && day <= 6)) {
    return { themeName: 'Silvester & Neujahr', emojis: ['🎆', '🎇', '✨', '🍾', '🥂', '🎉'] };
  }
  // 2. Weihnachten (01.12. - 26.12.)
  if (month === 12 && day <= 26) {
    return { themeName: 'Weihnachten', emojis: ['❄️', '⛄', '🎄', '🎁', '✨', '🔔'] };
  }
  // 3. Advent / Spätherbst (03.11. - 30.11.)
  if (month === 11 && day >= 3) {
    return { themeName: 'Advent & Vorweihnachten', emojis: ['🍁', '❄️', '🕯️', '☕', '✨', '🍪'] };
  }
  // 4. Halloween (24.10. - 02.11.)
  if ((month === 10 && day >= 24) || (month === 11 && day <= 2)) {
    return { themeName: 'Halloween', emojis: ['🎃', '👻', '🦇', '🕷️', '🕸️', '🍬'] };
  }
  // 5. Herbst (01.09. - 23.10.)
  if (month === 9 || (month === 10 && day < 24)) {
    return { themeName: 'Herbst', emojis: ['🍂', '🍁', '🍄', '🌰', '🦔', '🍎'] };
  }
  // 6. Sommer (01.06. - 31.08.)
  if (month >= 6 && month <= 8) {
    return { themeName: 'Sommer', emojis: ['☀️', '🍦', '🏖️', '🍉', '🕶️', '🌴'] };
  }
  // 7. Frühling & Ostern (01.03. - 31.05.)
  if (month >= 3 && month <= 5) {
    return { themeName: 'Frühling & Ostern', emojis: ['🌸', '🌷', '🐣', '🐰', '🌼', '🌱'] };
  }
  // 8. Winter (Rest von Jan & Feb)
  return { themeName: 'Winter', emojis: ['❄️', '❅', '❆', '⛄', '🧣', '🧤'] };
}

// Global cached config to avoid spamming the backend API
let cachedConfig: ThemeConfig | null = null;
let lastFetchTime = 0;

export async function fetchThemeConfig(): Promise<ThemeConfig> {
  const now = Date.now();
  if (cachedConfig && now - lastFetchTime < 30000) {
    return cachedConfig;
  }
  try {
    const data = await apiFetch('/system/theme');
    if (data && typeof data.enabled === 'boolean') {
      cachedConfig = data;
      lastFetchTime = now;
      return cachedConfig as ThemeConfig;
    }
  } catch {}
  return DEFAULT_THEME_CONFIG;
}

export function isUserThemeEffectDisabled(): boolean {
  try {
    return sessionStorage.getItem('theme_effect_disabled') === 'true';
  } catch {
    return false;
  }
}

export function setUserThemeEffectDisabled(disabled: boolean): void {
  try {
    if (disabled) {
      sessionStorage.setItem('theme_effect_disabled', 'true');
    } else {
      sessionStorage.removeItem('theme_effect_disabled');
    }
  } catch {}
  window.dispatchEvent(new Event('seasonal-effect-toggle'));
}

interface Particle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  emoji: string;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  swayAmp: number;
  swayFreq: number;
  swayOffset: number;
}

interface SeasonalEffectProps {
  variant: 'dashboard' | 'header' | 'preview';
  customConfig?: ThemeConfig;
  height?: number; // for preview
}

export const SeasonalEffect: React.FC<SeasonalEffectProps> = ({
  variant,
  customConfig,
  height,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [config, setConfig] = useState<ThemeConfig>(customConfig || DEFAULT_THEME_CONFIG);
  const [userDisabled, setUserDisabled] = useState<boolean>(isUserThemeEffectDisabled);

  useEffect(() => {
    if (customConfig) {
      setConfig(customConfig);
      return;
    }
    let isMounted = true;
    fetchThemeConfig().then((cfg) => {
      if (isMounted) setConfig(cfg);
    });

    const handleToggle = () => {
      setUserDisabled(isUserThemeEffectDisabled());
    };
    window.addEventListener('seasonal-effect-toggle', handleToggle);
    return () => {
      isMounted = false;
      window.removeEventListener('seasonal-effect-toggle', handleToggle);
    };
  }, [customConfig]);

  useEffect(() => {
    // If disabled globally or by user in session (unless preview)
    if (variant !== 'preview') {
      if (!config.enabled || userDisabled) return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let width = 0;
    let canvasHeight = 0;

    const isHeader = variant === 'header';
    const isDashboard = variant === 'dashboard';

    // Particle count based on density and variant
    let count = isHeader ? 12 : 30;
    if (config.density === 'low') count = isHeader ? 7 : 16;
    if (config.density === 'high') count = isHeader ? 22 : 55;

    // Emojis to display
    const emojis =
      config.mode === 'auto' ? getAutoThemeEmojis().emojis : config.emojis || DEFAULT_THEME_CONFIG.emojis;

    // Speed multiplier
    let baseSpeed = isHeader ? 0.6 : 1.0;
    if (config.speed === 'slow') baseSpeed *= 0.65;
    if (config.speed === 'fast') baseSpeed *= 1.5;

    // Wind multiplier
    let windOffset = 0;
    if (config.wind === 'light') windOffset = 0.3;
    if (config.wind === 'gentle') windOffset = 0.6;

    const particles: Particle[] = [];

    const resize = () => {
      if (isDashboard) {
        width = window.innerWidth;
        canvasHeight = window.innerHeight;
      } else if (containerRef.current) {
        width = containerRef.current.offsetWidth || window.innerWidth;
        canvasHeight = height || containerRef.current.offsetHeight || 60;
      } else {
        width = window.innerWidth;
        canvasHeight = height || 60;
      }
      canvas.width = width;
      canvas.height = canvasHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    // Initialize particles
    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * canvasHeight, // spread out initially
        size: isHeader ? 11 + Math.random() * 7 : 14 + Math.random() * 14,
        speedY: (0.7 + Math.random() * 1.1) * baseSpeed,
        speedX: (-0.2 + Math.random() * 0.4 + windOffset) * baseSpeed,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (-0.02 + Math.random() * 0.04) * (isHeader ? 0.5 : 1),
        opacity: isHeader ? 0.55 + Math.random() * 0.35 : 0.65 + Math.random() * 0.35,
        swayAmp: 0.5 + Math.random() * 1.2,
        swayFreq: 0.015 + Math.random() * 0.025,
        swayOffset: Math.random() * 100,
      });
    }

    let frame = 0;

    const render = () => {
      frame++;
      ctx.clearRect(0, 0, width, canvasHeight);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // Horizontal sway
        const sway = Math.sin((frame + p.swayOffset) * p.swayFreq) * p.swayAmp;
        p.x += p.speedX + sway;
        p.y += p.speedY;
        p.rotation += p.rotationSpeed;

        // In header mode: fade out gently near bottom edge so it disappears cleanly inside header
        let currentAlpha = p.opacity;
        if (isHeader) {
          if (p.y > canvasHeight - 20) {
            currentAlpha = Math.max(0, p.opacity * ((canvasHeight - p.y) / 20));
          }
        }

        // Draw emoji particle
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = currentAlpha;
        ctx.font = `${Math.floor(p.size)}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(p.emoji, 0, 0);
        ctx.restore();

        // Respawn when fallen out of bounds
        if (p.y > canvasHeight + 15 || p.x < -30 || p.x > width + 30) {
          p.x = Math.random() * width;
          p.y = -20;
          p.emoji = emojis[Math.floor(Math.random() * emojis.length)];
          p.speedY = (0.7 + Math.random() * 1.1) * baseSpeed;
        }
      }

      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [config, userDisabled, variant, height]);

  // Don't render anything if globally or locally disabled (except in preview)
  if (variant !== 'preview' && (!config.enabled || userDisabled)) {
    return null;
  }

  if (variant === 'dashboard') {
    return (
      <div className="pointer-events-none fixed inset-0 z-20 overflow-hidden" aria-hidden="true">
        <canvas ref={canvasRef} className="block w-full h-full" />
      </div>
    );
  }

  // Header or preview mode: absolutely positioned inside parent container
  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
};

export default SeasonalEffect;
